import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { products } from "@/mocks/products";
import { useCart } from "@/hooks/useCart";
import { StarRating } from "@/components/base/ProductCard";

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const product = products.find((p) => p.id === id);
  const { addToCart } = useCart();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [activeTab, setActiveTab] = useState<"details" | "specs" | "reviews">("details");

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 text-center py-20">
          <h2 className="text-2xl font-bold text-gray-700">Product not found</h2>
          <Link to="/products" className="mt-4 inline-block text-orange-500 hover:underline">Back to Products</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const discount = Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100);

  const handleAddToCart = () => {
    addToCart({ productId: product.id, quantity, price: product.price, name: product.name, image: product.images[0] });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const related = products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-8">
            <Link to="/" className="hover:text-orange-500 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/products" className="hover:text-orange-500 transition-colors">Products</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/products?category=${product.category}`} className="hover:text-orange-500 transition-colors capitalize">{product.category.replace("-", " ")}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-800 line-clamp-1">{product.name}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Images */}
            <div>
              <div className="rounded-xl overflow-hidden bg-gray-50 mb-3 aspect-square">
                <img src={product.images[selectedImage]} alt={product.name} className="w-full h-full object-cover object-top" />
              </div>
              <div className="grid grid-cols-6 gap-2">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className={`rounded-lg overflow-hidden aspect-square border-2 transition-colors cursor-pointer ${selectedImage === i ? "border-orange-500" : "border-transparent hover:border-gray-300"}`}
                  >
                    <img src={img} alt={`View ${i + 1}`} className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            </div>

            {/* Info */}
            <div>
              {product.badge && (
                <span className="inline-block px-3 py-1 bg-orange-100 text-orange-600 text-xs font-semibold rounded-full mb-3">{product.badge}</span>
              )}
              <h1 className="text-3xl font-black text-gray-900 mb-3 leading-tight">{product.name}</h1>
              <div className="flex items-center gap-3 mb-4">
                <StarRating rating={product.rating} size="text-sm" />
                <span className="text-gray-500 text-sm">({product.reviewCount} reviews)</span>
                <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full">In Stock</span>
              </div>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-4xl font-black text-gray-900">${product.price}</span>
                {product.originalPrice > product.price && (
                  <>
                    <span className="text-xl text-gray-400 line-through">${product.originalPrice}</span>
                    <span className="px-2 py-0.5 bg-red-100 text-red-600 text-sm font-semibold rounded-md">Save {discount}%</span>
                  </>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-5">SKU: {product.sku}</p>
              <p className="text-gray-600 leading-relaxed mb-6">{product.shortDesc}</p>

              {/* Quantity + Cart */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-11 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer">
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="w-12 text-center font-semibold text-gray-800">{quantity}</span>
                  <button onClick={() => setQuantity(quantity + 1)} className="w-10 h-11 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer">
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                <button
                  onClick={handleAddToCart}
                  className={`flex-1 py-3 font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap ${added ? "bg-green-500 text-white" : "bg-orange-500 text-white hover:bg-orange-600"}`}
                >
                  {added ? <><i className="ri-check-line mr-2"></i>Added to Cart!</> : <><i className="ri-shopping-cart-line mr-2"></i>Add to Cart</>}
                </button>
              </div>
              <Link
                to="/cart"
                className="block w-full py-3 text-center font-bold border-2 border-gray-900 text-gray-900 rounded-lg hover:bg-gray-900 hover:text-white transition-all cursor-pointer whitespace-nowrap mb-6"
              >
                View Cart & Checkout
              </Link>

              {/* Trust */}
              <div className="grid grid-cols-3 gap-3 p-4 bg-gray-50 rounded-xl">
                <div className="text-center">
                  <i className="ri-shield-check-line text-green-500 text-xl block mb-1"></i>
                  <span className="text-xs text-gray-600">Secure Payment</span>
                </div>
                <div className="text-center">
                  <i className="ri-truck-line text-orange-500 text-xl block mb-1"></i>
                  <span className="text-xs text-gray-600">Fast Shipping</span>
                </div>
                <div className="text-center">
                  <i className="ri-refresh-line text-blue-500 text-xl block mb-1"></i>
                  <span className="text-xs text-gray-600">Easy Returns</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-8">
            <div className="flex gap-1">
              {(["details", "specs", "reviews"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 text-sm font-semibold capitalize transition-colors cursor-pointer whitespace-nowrap border-b-2 -mb-px ${activeTab === tab ? "border-orange-500 text-orange-500" : "border-transparent text-gray-500 hover:text-gray-800"}`}
                >
                  {tab === "reviews" ? `Reviews (${product.reviews.length})` : tab}
                </button>
              ))}
            </div>
          </div>

          {activeTab === "details" && (
            <div className="max-w-3xl">
              <p className="text-gray-600 leading-relaxed text-base">{product.description}</p>
            </div>
          )}

          {activeTab === "specs" && (
            <div className="max-w-2xl">
              <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
                {Object.entries(product.specs).map(([key, val]) => (
                  <div key={key} className="flex items-center px-5 py-3">
                    <span className="w-40 text-sm font-semibold text-gray-700 flex-shrink-0">{key}</span>
                    <span className="text-sm text-gray-600">{val}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "reviews" && (
            <div className="max-w-3xl space-y-5">
              {product.reviews.map((review) => (
                <div key={review.id} className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-full text-white font-bold text-sm flex-shrink-0">
                        {review.avatar}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 text-sm">{review.author}</div>
                        <div className="text-gray-400 text-xs">{review.date}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StarRating rating={review.rating} />
                      {review.verified && <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Verified</span>}
                    </div>
                  </div>
                  <p className="font-semibold text-gray-800 text-sm mb-1">{review.title}</p>
                  <p className="text-gray-600 text-sm leading-relaxed">{review.comment}</p>
                </div>
              ))}
            </div>
          )}

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-16">
              <h3 className="text-2xl font-black text-gray-900 mb-6">Related Products</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                {related.map((p) => (
                  <Link key={p.id} to={`/product/${p.id}`} className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-orange-200 transition-all cursor-pointer">
                    <div className="aspect-square overflow-hidden bg-gray-50">
                      <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-semibold text-gray-800 line-clamp-2 mb-1">{p.name}</p>
                      <p className="text-orange-500 font-bold">${p.price}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProductDetailPage;
